"use client"

import { useCallback, useEffect, useRef, useState } from "react"

export type ChatRole = "user" | "assistant" | "system"
export type ChatMessage = { role: ChatRole; content: string }
export type ChatLocale = "ua" | "ru" | "en"

const MAX_HISTORY = 20
const MAX_CONTENT_LENGTH = 2000

type UseAiChatOptions = {
  locale: ChatLocale
}

type UseAiChatReturn = {
  messages: ChatMessage[]
  loading: boolean
  error: string | null
  retryAfter: number
  send: (text: string) => Promise<void>
  reset: () => void
}

const errorText = (locale: ChatLocale, key: "network" | "invalid" | "server") => {
  const m: Record<ChatLocale, Record<typeof key, string>> = {
    ua: {
      network: "Помилка зв'язку, спробуйте ще раз",
      invalid: "Некоректний запит",
      server: "Сервер тимчасово недоступний",
    },
    ru: {
      network: "Ошибка связи, попробуйте ещё раз",
      invalid: "Некорректный запрос",
      server: "Сервер временно недоступен",
    },
    en: {
      network: "Connection error, try again",
      invalid: "Invalid request",
      server: "Server temporarily unavailable",
    },
  }
  return m[locale][key]
}

export function useAiChat({ locale }: UseAiChatOptions): UseAiChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [retryAfter, setRetryAfter] = useState(0)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (retryAfter <= 0) return
    const t = setInterval(() => {
      setRetryAfter((s) => (s <= 1 ? 0 : s - 1))
    }, 1000)
    return () => clearInterval(t)
  }, [retryAfter])

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim()
      if (!trimmed || loading || retryAfter > 0) return
      if (trimmed.length > MAX_CONTENT_LENGTH) {
        setError(errorText(locale, "invalid"))
        return
      }

      const userMsg: ChatMessage = { role: "user", content: trimmed }
      const nextHistory = [...messages, userMsg].slice(-MAX_HISTORY)
      setMessages(nextHistory)
      setError(null)
      setLoading(true)

      abortRef.current?.abort()
      const ctrl = new AbortController()
      abortRef.current = ctrl

      try {
        const backend = process.env.NEXT_PUBLIC_MEDUSA_BACKEND_URL || ""
        const key = process.env.NEXT_PUBLIC_MEDUSA_PUBLISHABLE_KEY || ""
        const res = await fetch(`${backend}/store/ai/chat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-publishable-api-key": key,
          },
          body: JSON.stringify({ messages: nextHistory, locale }),
          signal: ctrl.signal,
        })

        if (res.status === 429) {
          const ra = parseInt(res.headers.get("retry-after") || "60", 10)
          setRetryAfter(Number.isFinite(ra) && ra > 0 ? ra : 60)
          setMessages(messages)
          return
        }

        if (!res.ok) {
          setMessages(messages)
          setError(
            errorText(locale, res.status >= 400 && res.status < 500 ? "invalid" : "server")
          )
          return
        }

        const data = (await res.json()) as { reply?: string }
        if (!data.reply) {
          setMessages(messages)
          setError(errorText(locale, "server"))
          return
        }
        setMessages([
          ...nextHistory,
          { role: "assistant", content: data.reply },
        ])
      } catch (e) {
        if ((e as Error).name === "AbortError") return
        setMessages(messages)
        setError(errorText(locale, "network"))
      } finally {
        setLoading(false)
      }
    },
    [messages, loading, retryAfter, locale]
  )

  const reset = useCallback(() => {
    abortRef.current?.abort()
    setMessages([])
    setError(null)
    setRetryAfter(0)
  }, [])

  return { messages, loading, error, retryAfter, send, reset }
}

export function normalizeLocale(countryCode?: string): ChatLocale {
  if (countryCode === "ru") return "ru"
  if (countryCode === "en") return "en"
  return "ua"
}
